import { CircularProgress, Popover } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useGetDoorBookingsQuery } from '../../api/door/doorHistoryApiSlice';
import { DoorBooking } from '../../api/door/doorApiTypes';
import { makeBookingsForDoorUrl } from '../../routes';
import { formatDateTime } from '../../utils';
import { useLocationTimeZone } from '../../hooks/useLocationTimeZone';
import { formatErrorDetail } from '../../utils/errorDetail';

const RECENT_BOOKING_COUNT = 5;

interface DoorBookingsPopoverProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    depotId: string;
    /** The station's location — its zone (#3219) is the clock the windows are shown on. */
    locationId?: string;
    door: number;
    /** The booking currently holding this Fach, if any — the depot view
     *  already knows it from the door state, so the row can be marked without
     *  the endpoint deriving a status of its own. */
    currentOrderId?: string;
    onSelectBooking: (booking: DoorBooking) => void;
}

/**
 * "Who had this Fach?" — the last few bookings of one locker, opened from the
 * locker card. The current holder is marked at the top; anything deeper than
 * five rows continues in the bookings overview, pre-filtered to this Fach.
 */
const DoorBookingsPopover = (props: DoorBookingsPopoverProps) => {
    const { t, i18n } = useTranslation();
    const timeZone = useLocationTimeZone(props.locationId);
    const navigate = useNavigate();
    const [detailsShown, setDetailsShown] = useState(false);

    // Refetched on every open rather than served from cache: rebooking a
    // customer onto another Fach writes a new allocation row, and that path is
    // a saga rather than an RTK mutation, so no tag invalidation reaches this
    // slice. DoorListItem mounts this component only while the popover is
    // open, so "on mount" is "on open".
    const { data, isFetching, isError, error } = useGetDoorBookingsQuery(
        { depotId: props.depotId, door: props.door, limit: RECENT_BOOKING_COUNT },
        { refetchOnMountOrArgChange: true },
    );

    const bookings = data?.bookings ?? [];

    const formatWindow = (booking: DoorBooking) => {
        const language = i18n.language === 'de' ? 'de' : 'en';
        const start = booking.startDate
            ? formatDateTime(booking.startDate, language, {
                includeTime: true,
                timeFormat: 'short',
                timeZone,
            })
            : t('common.not_applicable');
        const end = booking.endDate
            ? formatDateTime(booking.endDate, language, {
                includeTime: true,
                timeFormat: 'short',
                timeZone,
            })
            : t('common.not_applicable');
        return `${start} – ${end}`;
    };

    const renderBody = () => {
        if (isFetching) {
            return (
                <div className={'flex justify-center py-6'}>
                    <CircularProgress size={20} />
                </div>
            );
        }

        // An error must not read like "this Fach was never used" — the two
        // look identical otherwise, and a silent empty state is how a broken
        // read path stays unreported (#1579/#1581).
        //
        // The technical detail sits behind an explicit toggle and is off by
        // default. It exists so an operator can relay the cause when reporting
        // a problem, not so they have to understand it: rendering it inline put
        // a raw HTML error page in front of someone who only wanted to know why
        // a list was empty (PO, 2026-08-29). The plain sentence is the message;
        // the detail is available when it is asked for.
        if (isError) {
            const detail = formatErrorDetail(error);
            return (
                <div className={'py-4'}>
                    <p className={'text-sm text-foreground/60'}>
                        {t('depot_details.door_bookings.error')}
                    </p>
                    {detail && (
                        <>
                            <button
                                className={'mt-1 text-xs text-primary hover:underline'}
                                onClick={() => setDetailsShown((shown) => !shown)}
                            >
                                {detailsShown
                                    ? t('common.hide_details')
                                    : t('common.show_details')}
                            </button>
                            {detailsShown && (
                                <p className={'mt-1 text-xs text-foreground/40 break-words'}>
                                    {detail}
                                </p>
                            )}
                        </>
                    )}
                </div>
            );
        }

        if (!bookings.length) {
            return (
                <p className={'text-sm text-foreground/60 py-4'}>
                    {t('depot_details.door_bookings.empty')}
                </p>
            );
        }

        return (
            <ul className={'divide-y divide-foreground/5'}>
                {bookings.map((booking) => {
                    const isCurrent =
                        !!props.currentOrderId &&
                        booking.orderId === props.currentOrderId;
                    return (
                        <li key={booking.allocationId}>
                            <button
                                className={
                                    'w-full text-left py-2 px-1 rounded hover:bg-foreground/5 ' +
                                    'transition-colors focus:outline-none focus:bg-foreground/5'
                                }
                                onClick={() => props.onSelectBooking(booking)}
                            >
                                <span className={'flex items-center gap-2'}>
                                    <span className={'text-sm font-semibold text-foreground'}>
                                        {booking.bookingNumber}
                                    </span>
                                    {isCurrent && (
                                        <span
                                            className={
                                                'text-[11px] px-2 py-0.5 rounded-full ' +
                                                'bg-primary text-text-buttonContrast'
                                            }
                                        >
                                            {t('depot_details.door_bookings.current')}
                                        </span>
                                    )}
                                </span>
                                <span className={'block text-xs text-foreground/60'}>
                                    {formatWindow(booking)}
                                </span>
                                <span className={'block text-xs text-foreground/60 truncate'}>
                                    {booking.customerName
                                        ?? t('depot_details.door_bookings.anonymized')}
                                    {booking.email ? ` · ${booking.email}` : ''}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        );
    };

    return (
        <Popover
            open={props.open}
            anchorEl={props.anchorEl}
            onClose={props.onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { className: 'rounded-[5px] shadow-sm' } }}
        >
            <div className={'w-80 max-w-[90vw] p-3'}>
                <h4 className={'text-sm font-semibold text-foreground mb-2'}>
                    {t('depot_details.door_bookings.title', { door: props.door })}
                </h4>
                {renderBody()}
                <button
                    className={'mt-2 text-sm text-primary hover:underline'}
                    onClick={() => {
                        props.onClose();
                        navigate(makeBookingsForDoorUrl(props.door));
                    }}
                >
                    {t('depot_details.door_bookings.show_all', { door: props.door })}
                </button>
            </div>
        </Popover>
    );
};

export default DoorBookingsPopover;
